/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import Helmet from 'react-helmet'

import {
    normalizeSetBundleProduct,
    getUpdateBundleChildArray
} from '@salesforce/retail-react-app/app/utils/product-utils'
import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useProduct,
    useProducts,
    useCategory,
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import InformationAccordion from '@salesforce/retail-react-app/app/pages/product-detail/partials/information-accordion'
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useCurrentBasket} from '../../../hooks/use-current-basket'
import {useVariant} from '../../../hooks/use-variant'
import {useWishList} from '../../../hooks/use-wish-list'
import {useSiteCustomPreferences} from '../../../hooks/use-site-custom-preferences.js'
import {useTrackThinkAnalyticsEvents} from '../../../hooks/use-track-thinkanalytics-events.js'
import useSegment from '../../../integrations/segment/app/hooks/use-segment.js'
import {useGtm} from '../../../integrations/gtm/app/hooks/use-gtm.js'

import ProductView from '../../../components/product-view'
import EinsteinTaSlotHtml from '../../../components/content-slot/einstein-ta-slot-html'
import {productCarouselStyles} from '../../../theme/components/project/product-carousel'
import {
    getPowerReviewAPIKeys,
    initPowerReviewsForPLPAndPDP
} from '../../../utils/power-reviews-utils'
import {extractProductId} from '../../../utils/url.js'
import {API_ERROR_MESSAGE, MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '../../../constants'

const ProductContent = () => {
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const {formatMessage} = useIntl()
    const history = useHistory()
    const location = useLocation()
    const activeData = useActiveData()
    const {locale, buildUrl} = useMultiSite()
    const segment = useSegment()
    const gtm = useGtm()
    const einstein = useEinstein()
    const {mutate: trackTAEvent} = useTrackThinkAnalyticsEvents()
    const isTAEnabled = getcustomPreferencevalue('pwaThinkAnalyticsEnabled')
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const {res} = useServerContext()
    const styles = useMultiStyleConfig('Header')

    const [isAddingToCart, setIsAddingToCart] = useState(false)

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    /*************************** Product Detail and Category ********************/
    const {slug} = useParams()
    const productId = extractProductId(slug)
    const urlParams = new URLSearchParams(location.search)
    const {
        data: product,
        isLoading: isProductLoading,
        isError: isProductError,
        error: productError
    } = useProduct(
        {
            parameters: {
                id: urlParams.get('pid') || productId,
                perPricebook: true,
                expand: [
                    'availability',
                    'promotions',
                    'options',
                    'images',
                    'prices',
                    'variations',
                    'set_products',
                    'bundled_products',
                    'page_meta_tags'
                ],
                allImages: true
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const isSubscriptionProduct = product?.c_subscriptionProduct || false
    const primaryCategoryId = isSubscriptionProduct ? 'memberships' : product?.primaryCategoryId
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: primaryCategoryId,
            levels: 1
        }
    })

    /****************************** Sets and Bundles *********************************/
    const [childProductSelection, setChildProductSelection] = useState({})
    const [childProductOrderability, setChildProductOrderability] = useState({})
    const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1)
    const childProductRefs = React.useRef({})
    const isProductASet = product?.type.set
    const isProductABundle = product?.type.bundle

    let bundleChildVariantIds = ''
    if (isProductABundle)
        bundleChildVariantIds = Object.keys(childProductSelection)
            ?.map((key) => childProductSelection[key].variant.productId)
            .join(',')

    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true
        }
    )

    if (isProductABundle && bundleChildrenData) {
        // Loop through the bundle children and update the inventory for variant selection
        product.bundledProducts.forEach(({product: childProduct}, index) => {
            const matchingChildProduct = bundleChildrenData.data.find(
                (bundleChild) => bundleChild.id === childProduct.id
            )
            if (matchingChildProduct) {
                product.bundledProducts[index].product = {
                    ...childProduct,
                    inventory: matchingChildProduct.inventory
                }
            }
        })
    }

    const comboProduct = isProductASet || isProductABundle ? normalizeSetBundleProduct(product) : {}

    const containerStyles = {
        padding: {
            base: '0 10px',
            sm: '0 20px',
            lg: '0 64px'
        },
        maxWidth: {
            base: '100%',
            lg: '1920px'
        },
        margin: '0 auto'
    }

    /**************** Error Handling ****************/

    if (isProductError) {
        const errorStatus = productError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Product Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }
    if (isCategoryError) {
        const errorStatus = categoryError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Category Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    const variant = useVariant(product)

    /**************** Product Variant ****************/
    useEffect(() => {
        if (!variant) {
            return
        }
        if (product?.type?.item !== true) {
            // update the variation attributes parameter on
            // the url accordingly as the variant changes
            const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
                pid: variant?.productId
            })
            history.replace(updatedUrl)
        }
    }, [variant])

    /**************** Wishlist ****************/
    const {isLoading: isWishlistLoading} = useWishList()
    /**************** Add To Cart ****************/
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
            isClosable: false,
            duration: 3000
        })
    }

    const handleAddToCart = async (productSelectionValues) => {
        setIsAddingToCart(true)
        try {
            const productItems = productSelectionValues.map(({variant, quantity}) => ({
                productId: variant?.productId,
                price: variant?.price,
                quantity
            }))

            await addItemToNewOrExistingBasket(productItems)

            // gtm
            gtm.sendAddToCart('addToCart', productSelectionValues[0]?.product)
            // segment
            segment.sendAddToCart(productSelectionValues[0]?.product, basket)
            // TA
            if (isTAEnabled) {
                trackTAEvent({
                    c_productId: productSelectionValues[0]?.product?.id,
                    c_refUseCase: 'CRSTORE',
                    c_useCase: 'Store%2FLearn%2FAddToCart'
                })
            }

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            logger.error('error', error)
            showError(error)
        } finally {
            // Reset loading state once the function completes
            setIsAddingToCart(false)
        }
    }

    /**************** Product Set/Bundles Handlers ****************/
    const handleChildProductValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })

        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(childProductSelection)
        const firstUnselectedProduct = comboProduct.childProducts.find(
            ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
        )?.product

        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]

            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }

            return false
        }

        return true
    }, [product, childProductSelection])

    const handleProductSetAddToCart = () => {
        setIsAddingToCart(true)
        try {
            // Get all the selected products, and pass them to the addToCart handler which
            // accepts an array.
            const productSelectionValues = Object.values(childProductSelection)
            return handleAddToCart(productSelectionValues)
        } catch (error) {
            logger.error(
                'Something went wrong while adding the product to the cart. Please try again:',
                error
            )
        } finally {
            // Reset loading state once the function completes
            setIsAddingToCart(false)
        }
    }

    /**************** Product Bundle Handlers ****************/
    // Top level bundle does not have variants
    const handleProductBundleAddToCart = async (variant, selectedQuantity) => {
        setIsAddingToCart(true)
        try {
            const childProductSelections = Object.values(childProductSelection)

            const productItems = [
                {
                    productId: product.id,
                    price: product.price,
                    quantity: selectedQuantity,
                    // The add item endpoint in the shopper baskets API does not respect variant selections
                    // for bundle children, so we have to make a follow up call to update the basket
                    // with the chosen variant selections
                    bundledProductItems: childProductSelections.map((child) => {
                        return {
                            productId: child.variant.productId,
                            quantity: child.quantity
                        }
                    })
                }
            ]

            const res = await addItemToNewOrExistingBasket(productItems)
            // TA
            if (isTAEnabled) {
                trackTAEvent({
                    c_productId: product?.id,
                    c_refUseCase: 'CRSTORE',
                    c_useCase: 'Store%2FLearn%2FAddToCart'
                })
            }

            // segment
            segment.sendAddToCart(product, basket)

            const bundleChildMasterIds = childProductSelections.map((child) => {
                return child.product.id
            })

            // since the returned data includes all products in basket
            // here we compare list of productIds in bundleProductItems of each productItem to filter out the
            // current bundle that was last added into cart
            const currentBundle = res.productItems.find((productItem) => {
                if (!productItem.bundledProductItems?.length) return
                const bundleChildIds = productItem.bundledProductItems?.map((item) => {
                    // seek out the bundle child that still uses masterId as product id
                    return item.productId
                })
                return bundleChildIds.every((id) => bundleChildMasterIds.includes(id))
            })

            const itemsToBeUpdated = getUpdateBundleChildArray(
                currentBundle,
                childProductSelections
            )

            if (itemsToBeUpdated.length) {
                // make a follow up call to update child variant selection for product bundle
                // since add item endpoint doesn't currently consider product bundle child variants
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: res.basketId
                    },
                    body: itemsToBeUpdated
                })
            }

            return childProductSelections
        } catch (error) {
            showError(error)
        } finally {
            // Reset loading state once the function completes
            setIsAddingToCart(false)
        }
    }

    /**************** Power Reviews ***********/
    const writeAReviewURL = buildUrl('/write-a-review')
    const powerReviewApiKey = getcustomPreferencevalue('pwaPowerReviewsApiKey')

    useEffect(() => {
        const showReviews = async () => {
            if (document.readyState !== 'complete') {
                await new Promise((resolve) => {
                    window.addEventListener('load', resolve, {once: true})
                })
            }

            const result = await getPowerReviewAPIKeys(getcustomPreferencevalue)
            if (!result) return

            try {
                const writeAReviewObject = {
                    api_key: result.powerReviewApiKey,
                    locale: locale?.id?.replace('-', '_') || 'en_US',
                    merchant_group_id: result.merchantGroupID,
                    merchant_id: result.merchantID,
                    review_wrapper_url: `${writeAReviewURL}?prProductID=${productId}&pr=true`,
                    page_id: productId,
                    components: {
                        ReviewSnippet: 'pr-reviewsnippet',
                        ReviewDisplay: 'pr-reviewdisplay'
                    }
                }
                await initPowerReviewsForPLPAndPDP(result, true, writeAReviewObject)
            } catch (err) {
                logger.error('Product Content', {
                    namespace: 'showTileRatings',
                    additionalProperties: {error: err.message}
                })
            }
        }
        if (powerReviewApiKey && productId) {
            showReviews()
        }

        return () => {
            const mounted = document.getElementById('pr-reviewsnippet')
            const reviewsDisplay = document.getElementById('pr-reviewdisplay')
            if (mounted) mounted.innerHTML = ''
            if (reviewsDisplay) reviewsDisplay.innerHTML = ''
        }
    }, [productId, powerReviewApiKey])

    /**************** Active data ****************/
    useEffect(() => {
        if (!product) return

        const trackView = (item) => {
            try {
                einstein.sendViewProduct(item)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product: item}
                })
            }
            activeData.sendViewProduct(category, item, 'detail')
        }

        if (product.type?.set && Array.isArray(product.setProducts)) {
            product.setProducts.forEach(trackView)
        } else {
            trackView(product)
        }
    }, [product])

    /***************** GTM *************/
    useEffect(() => {
        if (product && gtm) {
            gtm.sendPdpViewItem('pdp', product)
        }
    }, [product, gtm])

    return (
        <Box
            className="sf-product-detail-page"
            layerStyle="page"
            data-testid="product-details-page"
            padding="0 !important"
            bgColor="secondaryPageBgColor"
        >
            <Helmet>
                {product?.pageMetaTags?.map(({id, value}) => {
                    if (id === 'title') {
                        return <title key={id}>{value}</title>
                    }
                    return <meta name={id} content={value} key={id} />
                })}
                <meta property="og:title" content={product?.name} />
                <meta
                    property="og:image"
                    content={product?.imageGroups?.[0]?.images?.[0]?.disBaseLink}
                />
            </Helmet>

            <Box {...{...styles.container, className: 'pdppage-block'}}>
                {isProductASet || isProductABundle ? (
                    <Fragment>
                        <ProductView
                            product={product}
                            category={category}
                            addToCart={
                                isProductASet
                                    ? handleProductSetAddToCart
                                    : handleProductBundleAddToCart
                            }
                            isAddingToCart={isAddingToCart}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            isWishlistLoading={isWishlistLoading}
                            validateOrderability={handleChildProductValidation}
                            childProductOrderability={childProductOrderability}
                            setSelectedBundleQuantity={setSelectedBundleQuantity}
                            bundleChildProducts={comboProduct.childProducts}
                        />

                        {/* TODO: consider `childProduct.belongsToSet` */}
                        {
                            // Render the child products
                            comboProduct.childProducts.map(
                                ({product: childProduct, quantity: childQuantity}) => (
                                    <Box
                                        key={childProduct.id}
                                        data-testid="child-product"
                                        className="bundle-child-products"
                                        display={'none'}
                                    >
                                        <ProductView
                                            // Do not use an arrow function as we are manipulating the functions scope.
                                            ref={function (ref) {
                                                // Assign the "set" scope of the ref, this is how we access the internal
                                                // validation.
                                                childProductRefs.current[childProduct.id] = {
                                                    ref,
                                                    validateOrderability: this.validateOrderability
                                                }
                                            }}
                                            product={childProduct}
                                            isProductPartOfSet={isProductASet}
                                            isProductPartOfBundle={isProductABundle}
                                            childOfBundleQuantity={childQuantity}
                                            selectedBundleParentQuantity={selectedBundleQuantity}
                                            addToCart={
                                                isProductASet
                                                    ? (variant, quantity) =>
                                                          handleAddToCart([
                                                              {
                                                                  product: childProduct,
                                                                  variant,
                                                                  quantity
                                                              }
                                                          ])
                                                    : null
                                            }
                                            isAddingToCart={isAddingToCart}
                                            onVariantSelected={(product, variant, quantity) => {
                                                if (quantity) {
                                                    setChildProductSelection((previousState) => ({
                                                        ...previousState,
                                                        [product.id]: {
                                                            product,
                                                            variant,
                                                            quantity: isProductABundle
                                                                ? childQuantity
                                                                : quantity
                                                        }
                                                    }))
                                                } else {
                                                    const selections = {...childProductSelection}
                                                    delete selections[product.id]
                                                    setChildProductSelection(selections)
                                                }
                                            }}
                                            isProductLoading={isProductLoading}
                                            isBasketLoading={isBasketLoading}
                                            isWishlistLoading={isWishlistLoading}
                                            setChildProductOrderability={
                                                setChildProductOrderability
                                            }
                                        />
                                        <InformationAccordion product={childProduct} />

                                        <Box display={['none', 'none', 'none', 'block']}>
                                            <hr />
                                        </Box>
                                    </Box>
                                )
                            )
                        }
                    </Fragment>
                ) : (
                    <Fragment>
                        <ProductView
                            product={product}
                            category={category}
                            addToCart={(variant, quantity) =>
                                handleAddToCart([{product, variant, quantity}])
                            }
                            isAddingToCart={isAddingToCart}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            isWishlistLoading={isWishlistLoading}
                        />
                    </Fragment>
                )}
            </Box>

            {/* Product Recommendations */}
            <Box
                backgroundImage={`url(${getAssetUrl('static/img/bg_shop_bottom_halftone.png')})`}
                backgroundPosition="0 100%"
                backgroundSize="auto"
                backgroundRepeat="repeat-x"
                sx={productCarouselStyles}
            >
                <Box className={'einsteinRecommendations'} sx={containerStyles}>
                    <EinsteinTaSlotHtml
                        slotId={'cts-recommendations-m'}
                        context={'global'}
                        type={'html'}
                    />
                    <EinsteinTaSlotHtml
                        slotId={'product-recommendations-m'}
                        context={'global'}
                        type={'html'}
                    />
                    <EinsteinTaSlotHtml
                        slotId={'PDP-recommendations-3-m'}
                        context={'global'}
                        type={'html'}
                    />
                </Box>
            </Box>
        </Box>
    )
}

export default ProductContent
